import {AccountLayout, ASSOCIATED_TOKEN_PROGRAM_ID, MintLayout, Token} from '@solana/spl-token';
import {
    Account, Connection,
    PublicKey,
    SystemProgram,
    TransactionInstruction,
} from '@solana/web3.js';
import {
    LENDING_PROGRAM_ID,
    TOKEN_PROGRAM_ID,
    WRAPPED_SOL_MINT,
} from '../../utils/ids';
import {ObligationLayout, TokenAccount, TokenAccountParser} from '../../models';
import {MiningLayout} from "@/api/models/state/mining";
import {getAccountInfo} from "@/api/provider/accounts";
import {getConnection} from "@/api/context/connection";

/**
 * 创建一个可以持有SOL的新账户，同时关闭了其持有SOL代币的功能
 * @param instructions
 * @param cleanupInstructions
 * @param toCheck
 * @param payer
 * @param amount
 * @param signers
 */
export function ensureSplAccount(
    instructions: TransactionInstruction[],
    cleanupInstructions: TransactionInstruction[],
    toCheck: TokenAccount,
    payer: PublicKey,
    amount: number,
    signers: Account[]
) {
    if (!toCheck.info.isNative) {
        return toCheck.pubkey;
    }

    const account = createUninitializedAccount(
        instructions,
        payer,
        amount,
        signers
    );
    //初始化为可以持有SOL的账号
    instructions.push(
        Token.createInitAccountInstruction(
            TOKEN_PROGRAM_ID,
            WRAPPED_SOL_MINT,
            account,
            payer
        )
    );
    // 关闭该账号的的持币功能
    cleanupInstructions.push(
        Token.createCloseAccountInstruction(
            TOKEN_PROGRAM_ID,
            account,
            payer,
            payer,
            []
        )
    );

    return account;
}
export const DEFAULT_TEMP_MEM_SPACE = 65548;

export function createUninitializedAccount(
  instructions: TransactionInstruction[],
  payer: PublicKey,
  amount: number,
  signers: Account[],
) {
  const account = new Account();
  instructions.push(
    SystemProgram.createAccount({
      fromPubkey: payer,
      newAccountPubkey: account.publicKey,
      lamports: amount,
      space: AccountLayout.span,
      programId: TOKEN_PROGRAM_ID,
    }),
  );

  signers.push(account);

  return account.publicKey;
}


export async function createAssociatedTokenAccount(
    connection:Connection,
  instructions: TransactionInstruction[],
  payer: PublicKey,
  mint: PublicKey,
  owner: PublicKey,
) {
    const associatedTokenAccount = await Token.getAssociatedTokenAddress(
        ASSOCIATED_TOKEN_PROGRAM_ID,
        TOKEN_PROGRAM_ID,
        mint,
        owner
    )
    const accountInfo = await getAccountInfo(connection,associatedTokenAccount)
    if (accountInfo==null){
        instructions.push(Token.createAssociatedTokenAccountInstruction(
            ASSOCIATED_TOKEN_PROGRAM_ID,
            TOKEN_PROGRAM_ID,
            mint,
            associatedTokenAccount,
            owner,
            payer
        ))
    }

  return associatedTokenAccount;
}
export function createUninitializedObligationAccount(
    instructions: TransactionInstruction[],
    payer: PublicKey,
    amount: number,
    signers: Account[],
){
  const account = new Account();
  instructions.push(
      SystemProgram.createAccount({
        fromPubkey: payer,
        newAccountPubkey: account.publicKey,
        lamports: amount,
        space: ObligationLayout.span,
        programId: LENDING_PROGRAM_ID,
      }),
  );

  signers.push(account);

  return account.publicKey;
}
export function createUninitializedMiningAccount(
    instructions: TransactionInstruction[],
    payer: PublicKey,
    amount: number,
    signers: Account[],
){
    const account = new Account();
    instructions.push(
        SystemProgram.createAccount({
            fromPubkey: payer,
            newAccountPubkey: account.publicKey,
            lamports: amount,
            space: MiningLayout.span,
            programId: LENDING_PROGRAM_ID,
        }),
    );
    signers.push(account);
    return account.publicKey;
}
// TODO: check if one of to accounts needs to be native sol ... if yes unwrap it ...
export async function findOrCreateAccountByMint(
    payer: PublicKey,
    owner: PublicKey,
    instructions: TransactionInstruction[],
    cleanupInstructions: TransactionInstruction[],
    mint: PublicKey, // use to identify same type
    signers: Account[],
    account?:TokenAccount
): Promise<PublicKey> {
    let toAccount: PublicKey;
    if (account && !account.info.isNative){
        toAccount = account.pubkey;
    } else {
        const connection = await getConnection()
        // creating depositor pool account
        toAccount = await createAssociatedTokenAccount(
            connection,
            instructions,
            payer,
            mint,
            owner,
        );
        if (mint.equals(WRAPPED_SOL_MINT) && owner.equals(payer)) {
            cleanupInstructions.push(
                Token.createCloseAccountInstruction(
                    TOKEN_PROGRAM_ID,
                    toAccount,
                    owner,
                    owner,
                    []
                )
            );
        }
    }

    return toAccount;
}
